// btw-front/src/pages/AdminNoticeEdit.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import NoticeForm from "../components/admin/NoticeForm";
import {
  get as getNotice,
  update as updateNotice,
  remove as removeNotice,
  uploadAttachments,
  removeAttachment,
  downloadAttachment,
} from "../api/notices";

export default function AdminNoticeEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  // 조회 effect 는 t 를 ref 로 읽는다 — react-i18next 는 언어가 바뀔 때마다 t 를 새로 만들어,
  // 의존성에 두면 언어 전환만으로 재조회 → setForm 이 편집 중인 입력을 서버 값으로 덮는다.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  const [form, setForm] = useState({
    title: "",
    content: "",
    status: "draft",
    isPinned: false,
    isFeatured: false,
  });
  const [existing, setExisting] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getNotice(id)
      .then((n) => {
        if (cancelled) return;
        setForm({
          title: n.title || "",
          content: n.content || "",
          status: n.status || "draft",
          isPinned: !!n.isPinned,
          isFeatured: !!n.isFeatured,
        });
        setExisting(n.attachments || []);
      })
      .catch(() => {
        if (cancelled) return;
        message.error(tRef.current("admin.notices.toasts.loadFailed"));
        navigate("/admin", { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  async function handleDownloadExisting(a) {
    try {
      await downloadAttachment(id, a.id, a.originalName);
    } catch (e) {
      message.error(e.message || t("partners.notices.detail.downloadFailed"));
    }
  }

  function handleRemoveExisting(a) {
    Modal.confirm({
      title: t("admin.notices.form.remove"),
      content: a.originalName,
      okText: t("admin.notices.form.remove"),
      cancelText: t("admin.notices.form.cancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeAttachment(id, a.id);
          setExisting((prev) => prev.filter((x) => x.id !== a.id));
          message.success(t("admin.notices.toasts.attachmentRemoved"));
        } catch (e) {
          message.error(t("admin.notices.toasts.attachmentRemoveFailed"));
        }
      },
    });
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (submitting) return;
    if (!form.title.trim() || !form.content.trim()) {
      message.warning(t("admin.notices.toasts.required"));
      return;
    }
    setSubmitting(true);
    try {
      await updateNotice(id, {
        title: form.title,
        content: form.content,
        status: form.status,
        isPinned: form.isPinned,
        isFeatured: form.isFeatured,
      });
      if (newFiles.length) {
        try {
          await uploadAttachments(id, newFiles);
          setNewFiles([]);
        } catch (e) {
          message.warning(t("admin.notices.toasts.uploadFailed"));
        }
      }
      message.success(t("admin.notices.toasts.updated"));
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("admin.notices.toasts.updateFailed");
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function handleDelete() {
    Modal.confirm({
      title: t("admin.notices.deleteConfirmTitle"),
      content: t("admin.notices.deleteConfirmContent"),
      okText: t("admin.notices.deleteConfirmOk"),
      cancelText: t("admin.notices.deleteConfirmCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        if (deleting) return;
        setDeleting(true);
        try {
          await removeNotice(id);
          message.success(t("admin.notices.toasts.deleted"));
          navigate("/admin", { replace: true });
        } catch (e) {
          message.error(t("admin.notices.toasts.deleteFailed"));
        } finally {
          setDeleting(false);
        }
      },
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-4xl">
            <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
              {t("admin.notices.editTitle")}
            </h1>
            {loading ? (
              <p className="mt-8 text-center text-sm text-slate-400">{t("common.loading")}</p>
            ) : (
              <>
                <NoticeForm
                  form={form}
                  setForm={setForm}
                  newFiles={newFiles}
                  setNewFiles={setNewFiles}
                  existing={existing}
                  onRemoveExisting={handleRemoveExisting}
                  onDownloadExisting={handleDownloadExisting}
                  submitting={submitting}
                  onSubmit={handleSubmit}
                  cancelTo="/admin"
                  submitLabel={t("admin.notices.form.submit")}
                />

                {/* 위험 영역 — 삭제는 비가역이라 시각적으로 격리 */}
                <section className="mt-6 rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-rose-700">
                    {t("admin.notices.dangerZone")}
                  </h2>
                  <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-slate-700">
                      <p className="font-medium text-slate-900">
                        {t("admin.notices.deleteSectionTitle")}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {t("admin.notices.deleteSectionHint")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/40 disabled:opacity-60"
                      disabled={deleting}
                      onClick={handleDelete}
                    >
                      {t("admin.notices.delete")}
                    </button>
                  </div>
                </section>
              </>
            )}
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
